import { useCallback, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import classnames from 'classnames';
import { useFormContext } from 'react-hook-form';
import { Autocomplete } from '@material-ui/lab';
import CircularProgress from '@material-ui/core/CircularProgress';
import EmojiObjectsOutlinedIcon from '@material-ui/icons/EmojiObjectsOutlined';
import { msgFieldRequired, msgInvalidEmail, VALIDATE_EMAIL_REGEX } from '@Helpers';
import styles from './NominationForm.module.scss';
import { FieldNote } from '@Components/FieldNote/FieldNote';
import { TextInput } from '@Components/common/TextInput/TextInput';
import { NominationForm as NominationFormType, NominationMatch } from '@Types';
import { NominationErrorModal } from '@Views/NominationPage/NominationModal/NominationErrorModal';
import { RoutingPaths } from '@App/paths';

interface NotificationFormProps {
  isSubmitting?: boolean;
  onSubmit: (e?: React.FormEvent<HTMLFormElement | HTMLButtonElement>) => Promise<void>;
  errorCode: string;
  hideErrorModal: () => void;
  onNomineeNameChange: (searchTerm: string) => void;
  matches: NominationMatch[];
  areMatchesLoading: boolean;
  nominationFormData: NominationFormType;
}

export const NominationForm: React.VFC<NotificationFormProps> = ({
  nominationFormData,
  onSubmit,
  onNomineeNameChange,
  isSubmitting,
  matches,
  errorCode,
  hideErrorModal,
  areMatchesLoading,
}) => {
  const { t } = useTranslation('nominations');
  const { register, errors: fieldErrors, control, setValue } = useFormContext();
  const [isAutocompleted, setIsAutocompleted] = useState(false);

  const fundName = nominationFormData.fund_name;

  const getAutocompleteOption = useCallback((value: NominationMatch) => {
    let result = value.nominee_group_name;
    if (value.nominee_town_or_city) {
      result = `${result} - ${value.nominee_town_or_city}`;
    }
    return result;
  }, []);

  const handleSelectMatch = useCallback(
    (_, value) => {
      if (value) {
        setValue('group_id', value.group_id);
        setValue('invitation_id', value.invitation_id);
        // When autocompleted, hide fields we've already got from previous nominations.
        // We'll pass null to the backend, and it'll fill those out for us.
        // This also allows us to keep the email addresses of the groups private.
        setIsAutocompleted(true);
        setValue('nominee_email_address', null);
        setValue('nominee_town_or_city', null);
        setValue('nominee_website_or_social_media', null);
      } else {
        setValue('group_id', null);
        setValue('invitation_id', null);
        setIsAutocompleted(false);
      }
    },
    [setValue, setIsAutocompleted],
  );

  const handleNomineeNameChange = (searchTerm: string) => {
    setIsAutocompleted(false);
    onNomineeNameChange(searchTerm);
  };

  return (
    <form onSubmit={onSubmit}>
      <span className={styles.hintText}>{t('required_hint')}</span>
      <section className={styles.formSection}>
        <h2 className={styles.sectionHeader}>{t('about_nominee_section_header')}</h2>

        <span className={classnames(styles.inputLabel, styles.required)}>
          {t('nominee_name_label')}
        </span>
        <span className={classnames(styles.hintText, styles.inputLabelHint)}>
          {t('nominee_name_hint')}
        </span>
        <Autocomplete
          options={matches}
          getOptionLabel={(option) => option.nominee_group_name}
          renderOption={(option) => <>{getAutocompleteOption(option)}</>}
          clearOnBlur={false}
          disabled={isSubmitting}
          onChange={handleSelectMatch}
          freeSolo
          renderInput={(params) => (
            <TextInput
              {...params}
              placeholder={t('nominee_name_placeholder')}
              name="nominee_group_name"
              inputRef={register({ required: msgFieldRequired })}
              error={!!fieldErrors['nominee_group_name']}
              helperText={fieldErrors['nominee_group_name']?.message}
              disabled={isSubmitting}
              onChange={(e) => {
                handleNomineeNameChange(e.currentTarget.value);
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {areMatchesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {!isAutocompleted && (
          <>
            <span className={classnames(styles.inputLabel, styles.required)}>
              {t('nominee_city_label')}
            </span>
            <TextInput
              placeholder={t('nominee_city_placeholder')}
              name="nominee_town_or_city"
              inputRef={register({ required: msgFieldRequired })}
              error={!!fieldErrors['nominee_town_or_city']}
              helperText={fieldErrors['nominee_town_or_city']?.message}
              disabled={isSubmitting}
            />

            <span className={styles.inputLabel}>{t('nominee_email_label')}</span>
            <TextInput
              placeholder={t('nominee_email_placeholder')}
              type="email"
              name="nominee_email_address"
              inputRef={register({
                pattern: {
                  value: VALIDATE_EMAIL_REGEX,
                  message: msgInvalidEmail,
                },
              })}
              error={!!fieldErrors['nominee_email_address']}
              helperText={fieldErrors['nominee_email_address']?.message}
              disabled={isSubmitting}
            />

            <FieldNote
              className={classnames(styles.note)}
              icon={<EmojiObjectsOutlinedIcon style={{ fontSize: 15 }} />}
            >
              <Trans
                ns="nominations"
                i18nKey="nominee_email_hint"
                components={[<strong className={styles.textBold} />]}
              />
            </FieldNote>

            <span className={styles.inputLabel}>{t('nominee_social_label')}</span>
            <TextInput
              placeholder={t('nominee_social_placeholder')}
              name="nominee_website_or_social_media"
              inputRef={register}
              error={!!fieldErrors['nominee_website_or_social_media']}
              helperText={fieldErrors['nominee_website_or_social_media']?.message}
              disabled={isSubmitting}
              multiline
              maxRows={5}
            />
          </>
        )}
      </section>
      <section className={styles.formSection}>
        <h2 className={styles.sectionHeader}>{t('about_you_section_header')}</h2>

        <span className={classnames(styles.inputLabel, styles.required)}>
          {t('user_name_label')}
        </span>
        <TextInput
          placeholder={t('user_name_placeholder')}
          name="nominator_name"
          inputRef={register({
            required: msgFieldRequired,
          })}
          error={!!fieldErrors['nominator_name']}
          helperText={fieldErrors['nominator_name']?.message}
          disabled={isSubmitting}
        />

        <span className={classnames(styles.inputLabel, styles.required)}>
          {t('user_email_label')}
        </span>
        <TextInput
          placeholder={t('user_email_placeholder')}
          name="nominator_email"
          inputRef={register({
            required: msgFieldRequired,
            pattern: {
              value: VALIDATE_EMAIL_REGEX,
              message: msgInvalidEmail,
            },
          })}
          error={!!fieldErrors['nominator_email']}
          helperText={fieldErrors['nominator_email']?.message}
          disabled={isSubmitting}
        />

        <input type="hidden" name="group_id" ref={register()} />
        <input type="hidden" name="invitation_id" ref={register()} />

        <hr className={styles.divider} />
      </section>
      <div className={styles.legalSection}>
        <span>{t('actionfunder_info', { fund_name: fundName })}</span>
        <span>{t('agree_list_title')}</span>
        <ul className={styles.agreeList}>
          <li>
            <Trans
              ns="nominations"
              i18nKey="agree_privacy_item"
              components={[
                <a
                  href={RoutingPaths.PRIVACY_POLICY}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.link}
                />,
              ]}
            />
          </li>
        </ul>
      </div>
      <button
        type="button"
        className={styles.submitButton}
        onClick={onSubmit}
        disabled={isSubmitting}
      >
        {t('submit')}
      </button>
      <NominationErrorModal
        isOpen={errorCode !== ''}
        errorCode={errorCode}
        onClose={hideErrorModal}
      />
    </form>
  );
};
